/**
 * Track replay state store.
 * Manages timeline position, playback controls, and replay frame data.
 */

import { create } from 'zustand';
import type { ReplayFrame } from '@vtak/shared';

/** Replay store state and actions */
interface ReplayState {
  uid: string | null;
  callsign: string;
  frames: ReplayFrame[];
  currentIndex: number;
  playing: boolean;
  speed: number;
  loadFrames: (uid: string, callsign: string, frames: ReplayFrame[]) => void;
  play: () => void;
  pause: () => void;
  setSpeed: (speed: number) => void;
  seekTo: (index: number) => void;
  tick: () => void;
  reset: () => void;
}

/** Zustand store for track replay playback */
export const useReplayStore = create<ReplayState>((set) => ({
  uid: null,
  callsign: '',
  frames: [],
  currentIndex: 0,
  playing: false,
  speed: 1,

  loadFrames: (uid, callsign, frames) => set({
    uid,
    callsign,
    frames,
    currentIndex: 0,
    playing: false,
  }),

  play: () => set({ playing: true }),

  pause: () => set({ playing: false }),

  setSpeed: (speed) => set({ speed }),

  seekTo: (index) => set((state) => ({
    currentIndex: Math.max(0, Math.min(index, state.frames.length - 1)),
  })),

  tick: () => set((state) => {
    const nextIndex = state.currentIndex + 1;
    if (nextIndex >= state.frames.length) {
      return { playing: false };
    }
    return { currentIndex: nextIndex };
  }),

  reset: () => set({
    uid: null,
    callsign: '',
    frames: [],
    currentIndex: 0,
    playing: false,
    speed: 1,
  }),
}));
